
import React from 'react';
import { Page } from '../types';
import { useLanguage } from '../contexts/LanguageContext';
import { useSettings } from '../contexts/SettingsContext';
import ImageWithFallback from './ImageWithFallback';

interface FooterProps {
  onNavigate: (page: Page) => void;
  onNavigateShop: (category: string) => void;
}

const Footer: React.FC<FooterProps> = ({ onNavigate, onNavigateShop }) => {
  const { t } = useLanguage();
  const { settings } = useSettings();

  const contactRaw = settings?.contact_preview || {
    hotline: "090 123 4567",
    email: "squeakytown188@gmail.com"
  };
  const contact = {
    ...contactRaw,
    hotline: contactRaw.hotline || '090 123 4567',
    email: contactRaw.email || 'squeakytown188@gmail.com',
  };

  const LOGO_URL = "/logo.png";

  // Logic lấy link social từ settings
  const socialLinks = settings?.social_links || [];
  const facebookUrl = socialLinks.find(l => l.platform === 'facebook')?.url || "https://facebook.com/squeakytown";
  const instagramUrl = socialLinks.find(l => l.platform === 'instagram')?.url || "https://instagram.com/squeakytown";
  const zaloPhone = settings?.support_channels?.zalo_phone || contact.hotline;

  return (
    <footer className="bg-white border-t border-sage-light pt-16 pb-8">
      <div className="max-w-[1280px] mx-auto px-4 md:px-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-16">
          <div className="space-y-6">
            <div className="flex items-center gap-2 cursor-pointer group" onClick={() => onNavigate(Page.HOME)}>
              <ImageWithFallback
                src={settings?.branding?.logo_url || LOGO_URL}
                fallbackSrc={LOGO_URL}
                alt="Squeaky Town"
                className="h-10 w-auto object-contain blend-multiply transition-transform group-hover:scale-110"
              />
              <span className="font-display font-black text-xl text-primary tracking-tighter">Squeaky Town</span>
            </div>
            <p className="text-gray-500 text-sm font-medium leading-relaxed max-w-xs">
              {t('slogan')}
            </p>
            {/* Social Icons with Brand Colors */}
            <div className="flex gap-3">
              <a href={facebookUrl} target="_blank" rel="noreferrer" className="size-10 rounded-xl bg-[#1877F2] text-white flex items-center justify-center hover:-translate-y-1 transition-all shadow-lg shadow-blue-500/20">
                <svg className="size-5 fill-current" viewBox="0 0 24 24"><path d="M22 12c0-5.52-4.48-10-10-10S2 6.48 2 12c0 4.84 3.44 8.87 8 9.8V15H8v-3h2V9.5C10 7.57 11.57 6 13.5 6H16v3h-2c-.55 0-1 .45-1 1v2h3v3h-3v6.95c5.05-.5 9-4.76 9-9.95z" /></svg>
              </a>
              <a href={instagramUrl} target="_blank" rel="noreferrer" className="size-10 rounded-xl bg-[#E1306C] text-white flex items-center justify-center hover:-translate-y-1 transition-all shadow-lg shadow-pink-500/20">
                <svg className="size-5 fill-current" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.981 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" /></svg>
              </a>
              <a href={`https://zalo.me/${zaloPhone.replace(/\s/g, '')}`} target="_blank" rel="noreferrer" className="size-10 rounded-xl bg-[#0068FF] text-white flex items-center justify-center hover:-translate-y-1 transition-all shadow-lg shadow-blue-600/20">
                <svg className="size-5" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="11" fill="white" /><path d="M15.5 15.5H8.5V14.2L11.5 11.2H8.5V10H14.5V11L11.5 14.3H14.5V15.5H15.5Z" fill="#0068FF" /></svg>
              </a>
            </div>
          </div>

          <div>
            <h4 className="font-black text-earth mb-6 uppercase tracking-widest text-xs">{t('explore')}</h4>
            <ul className="space-y-4 text-sm font-bold text-gray-500">
              <li><button onClick={() => onNavigateShop('Tất cả')} className="hover:text-primary transition-colors">{t('newProducts')}</button></li>
              <li><button onClick={() => onNavigateShop('Combo')} className="hover:text-primary transition-colors">{t('savingCombos')}</button></li>
              <li><button onClick={() => onNavigate(Page.BLOG)} className="hover:text-primary transition-colors">{t('careBlog')}</button></li>
            </ul>
          </div>

          <div>
            <h4 className="font-black text-earth mb-6 uppercase tracking-widest text-xs">{t('support')}</h4>
            <ul className="space-y-4 text-sm font-bold text-gray-500">
              <li><button onClick={() => onNavigate(Page.SHIPPING_POLICY)} className="hover:text-primary transition-colors">{t('shippingPolicy')}</button></li>
              <li><button onClick={() => onNavigate(Page.RETURN_POLICY)} className="hover:text-primary transition-colors">{t('returnPolicy')}</button></li>
              <li>
                <button onClick={() => onNavigate(Page.DMCA)} className="hover:opacity-80 transition-opacity">
                  <img src="https://images.dmca.com/Badges/dmca_protected_sml_120n.png?ID=405c9c3e-d890-410c-a3c3-04494c25143a" alt="DMCA.com Protection Status" />
                </button>
              </li>

            </ul>
          </div>

          <div>
            <h4 className="font-black text-earth mb-6 uppercase tracking-widest text-xs">{t('contactInfo')}</h4>
            <ul className="space-y-4 text-sm font-bold text-gray-500">
              <li className="flex items-center gap-3">
                <span className="material-symbols-outlined text-primary">call</span>
                <a href={`tel:${contact.hotline.replace(/\s/g, '')}`} className="hover:text-primary transition-colors">{contact.hotline}</a>
              </li>
              <li className="flex items-center gap-3">
                <span className="material-symbols-outlined text-primary">mail</span>
                <a href={`mailto:${contact.email}`} className="hover:text-primary transition-colors">{contact.email}</a>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-sage-light pt-8 text-center md:text-left flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-4">
            <p className="text-xs font-bold text-gray-400">{t('rights')}</p>
            <button
              onClick={() => onNavigate(Page.ADMIN_LOGIN)}
              className="text-[10px] font-black text-gray-300 hover:text-primary uppercase tracking-widest"
            >
              Admin
            </button>
          </div>
          <p className="text-[10px] font-black text-gray-300 uppercase tracking-widest">Made with ♥ for Pets</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
